export type DocumentCategory =
  | 'credential'
  | 'certificate'
  | 'license'
  | 'identification'
  | 'training'
  | 'paper_record'
  | 'other';

export interface EmployeeDocument {
  document_id: number;
  fk_user_id: number;
  document_name: string;
  document_category: DocumentCategory;
  file_name: string;
  file_path: string;
  file_type: string | null;
  file_size: number | null;
  file_url: string | null;
  uploaded_by: number | null;
  uploader_name?: string;
  notes: string | null;
  expiry_date: string | null;
  days_until_expiry?: number | null;
  is_expired?: boolean;
  is_expiring_soon?: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Check if document is expired (today or earlier).
 */
export function isDocumentExpired(expiryDate: string | null | undefined): boolean {
  if (!expiryDate) return false;
  const expiry = new Date(expiryDate);
  if (isNaN(expiry.getTime())) return false;
  expiry.setHours(23, 59, 59, 999);
  return expiry.getTime() < Date.now();
}

/**
 * Check if document needs attention because it is expiring within 1 year (less than 1 year before expiry).
 */
export function isDocumentExpiringSoon(expiryDate: string | null | undefined): boolean {
  if (!expiryDate) return false;
  if (isDocumentExpired(expiryDate)) return false;
  const expiry = new Date(expiryDate);
  if (isNaN(expiry.getTime())) return false;
  const oneYearFromNow = new Date();
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
  return expiry.getTime() <= oneYearFromNow.getTime();
}

/**
 * Check if a document needs attention (either expired or expiring in less than 1 year).
 */
export function documentNeedsAttention(doc: { expiry_date?: string | null; is_expired?: boolean; is_expiring_soon?: boolean }): boolean {
  if (doc.is_expired || doc.is_expiring_soon) return true;
  if (!doc.expiry_date) return false;
  return isDocumentExpired(doc.expiry_date) || isDocumentExpiringSoon(doc.expiry_date);
}

export interface DocumentCategoryOption {
  value: DocumentCategory;
  label: string;
  badgeClass: string;
  description: string;
}

export const DOCUMENT_CATEGORIES: DocumentCategoryOption[] = [
  {
    value: 'credential',
    label: 'Credential',
    badgeClass: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800',
    description: 'Degrees, diplomas, and academic qualifications'
  },
  {
    value: 'certificate',
    label: 'Certificate',
    badgeClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    description: 'CPR, First Aid, specialized training certifications'
  },
  {
    value: 'license',
    label: 'License',
    badgeClass: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
    description: 'Nursing, CNA, driver license, professional practice'
  },
  {
    value: 'identification',
    label: 'Government ID',
    badgeClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    description: 'Passport, state ID, SSN card'
  },
  {
    value: 'paper_record',
    label: 'Paper Record',
    badgeClass: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300 border-rose-200 dark:border-rose-800',
    description: 'Scanned paper application, manual onboarding docs'
  },
  {
    value: 'training',
    label: 'Training & In-Service',
    badgeClass: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300 border-teal-200 dark:border-teal-800',
    description: 'In-service completion certificates, orientation forms'
  },
  {
    value: 'other',
    label: 'Other / General',
    badgeClass: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600',
    description: 'Miscellaneous employee documentation'
  }
];

export interface DocumentFilters {
  search?: string;
  category?: DocumentCategory | 'all' | '';
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface UpdateDocumentPayload {
  document_name: string;
  document_category: DocumentCategory;
  notes?: string | null;
  expiry_date?: string | null;
}

